import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';

/** How long a surface waits on PostHog before it falls back to the old UI. */
const HOLD_MS = 2000;

/**
 * Try it (CLE-593), PostHog flag `try_it_v1`, read fail-closed.
 *
 * `true`: the Try it UI (quotes, budget, Try card, results and Looks good).
 * `false`: the UI it replaces (Dry run, Sample 5 records, Check before
 * accepting, the record picker). `undefined`: PostHog is up but has not
 * answered yet, so a surface shows neither rather than flash the wrong one.
 * The hold ends after HOLD_MS, falling back to `false`, so a blocked PostHog
 * still gets the old UI. Without PostHog (tests) it is `false` at once.
 */
export function useTryIt(): boolean | undefined {
  const flag = useFeatureFlagEnabled('try_it_v1');
  const posthog = usePostHog();
  const loaded = Boolean(
    (posthog as { __loaded?: boolean } | undefined)?.__loaded
  );
  const [timedOut, setTimedOut] = useState(false);
  const holding = flag === undefined && loaded && !timedOut;
  useEffect(() => {
    if (!holding) return;
    const timer = setTimeout(() => setTimedOut(true), HOLD_MS);
    return () => clearTimeout(timer);
  }, [holding]);
  if (flag !== undefined) return flag === true;
  return holding ? undefined : false;
}
